import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import Conference from "./components/conference/Conference";
import Login from "./components/login/Login";
import UserPortal from "./components/user-portal/UserPortal";
// import Dashboard from "./components/Dashboard/dashboard";
import { SiteProvider, useSiteContext } from "./context/SiteContext";
import { WhiteLabelProvider } from "./context/WhiteLabelContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Recovery from "./components/Recovery/Recovery";
import RecoveryWithCode from "./components/Recovery/RecoveryWithCode";
import ForgotPassword from "./components/forgot-password/ForgotPassword";
import RoomNotFound from "./components/RoomNotFound";
import RecordingPreview from "./components/recording-preview/RecordingPreview";
import ClipPreview from "./components/recording-preview/ClipPreview";
import SsoLanding from "./components/sso/SsoLanding";
import ZapOAuthConsent from "./components/user-portal/pages/ZapOAuthConsent";
import { MeetingProvider } from "./context/MeetingContext";
import { DEFAULT_LOGO, DEFAULT_LOGO_ALT } from "./config/defaultLogo";
import { EditCtaProvider } from "./context/EditCtaContext";
import { BrandKitProvider } from "./context/BrandKitContext";
import WebinerBrandingPre from "./components/webiner-branding/WebinerBrandingPre";
import WebinerBrandingPost from "./components/webiner-branding/WebinerBrandingPost";
import WebinerWaiting from "./components/webiner-branding/WebinerWaiting";
import WebinarRegistrationPage from "./components/webinar-registration/WebinarRegistrationPage";
import EventLandingPublic from "./components/event-landing-public/EventLandingPublic";
import MaintenanceNotification from "./components/MaintenanceNotification";
import AppBannerMeta from "./components/common/AppBannerMeta";
import MobileAppBanner from "./components/common/MobileAppBanner";
import CutoverAnnouncementBar from "./components/common/CutoverAnnouncementBar";
import AcceptInvite from "./components/invite/AcceptInvite";

// Lazy load RecordingView — it's only used by Agora's headless browser
const RecordingView = lazy(() => import("./components/recording-view/RecordingView"));
const RecordingViewLite = lazy(() => import("./components/recording-view/RecordingViewLite"));
const RecordingViewLiteV3 = lazy(() => import("./components/recording-view/RecordingViewLiteV3"));

// Experimental — Apryse annotation + sync playground
const ApryseAnnotationPlayground = lazy(() =>
  import("./components/studio/apryse-playground/ApryseAnnotationPlayground")
);

// Component to handle maintenance mode redirect
function MaintenanceModeGuard({ children }) {
  const { maintenanceMode } = useSiteContext();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // If maintenance mode is enabled and user is not already on the maintenance page
    if (maintenanceMode && location.pathname !== '/maintenance-notification') {
      // Allow access to login and recording-view pages even during maintenance
      if (location.pathname !== '/login' && !location.pathname.startsWith('/recording-view')) {
        navigate('/maintenance', { replace: true });
      }
    }
  }, [maintenanceMode, location.pathname, navigate]);

  return children;
}

function App() {
  return (
    <>
      <BrowserRouter>
        <WhiteLabelProvider>
          <SiteProvider>
            <BrandKitProvider>
              <MeetingProvider>
                <EditCtaProvider>
                  <MaintenanceModeGuard>
                  <CutoverAnnouncementBar />
                  <AppBannerMeta />
                  <MobileAppBanner />
                  <Routes>
                  <Route
                    path="/apryse-playground"
                    element={
                      <Suspense fallback={<div style={{width:'100vw',height:'100vh',background:'#0D1117',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading playground…</div>}>
                        <ApryseAnnotationPlayground />
                      </Suspense>
                    }
                  />
                  {/* Invite landing — MUST be declared before the catch-all
                      :roomId route below, otherwise /invite/:token would be
                      treated as a meeting room ID. */}
                  <Route path="/invite/:token" element={<AcceptInvite />} />
                  <Route path=":roomId" element={<Conference />} />
                  <Route path="/sso" element={<SsoLanding />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/oauth/authorize" element={<ZapOAuthConsent />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/recovery" element={<Recovery />} />
                  <Route path="/recoveryc" element={<RecoveryWithCode />} />
                  <Route
                    path="/event/:slug"
                    element={<EventLandingPublic />}
                  />
                  <Route
                    path="/webinar/:id/event"
                    element={<WebinarRegistrationPage />}
                  />
                  {/* Keep /register for backward compatibility with old links */}
                  <Route
                    path="/webinar/:id/register"
                    element={<WebinarRegistrationPage />}
                  />
                  <Route
                    path="/webiner-branding-pre"
                    element={<WebinerBrandingPre />}
                  />
                  <Route
                    path="/webiner-branding-waiting"
                    element={<WebinerWaiting />}
                  />

                  <Route
                    path="/webiner-branding-post"
                    element={<WebinerBrandingPost />}
                  />
                  <Route
                    path="/maintenance"
                    element={<MaintenanceNotification />}
                  />

                  <Route path="/recoveryc" element={<RecoveryWithCode />} />
                  <Route path="/meeting-not-found" element={<RoomNotFound />} />
                  <Route
                    path="/recording-preview/:fileId"
                    element={<RecordingPreview />}
                  />
                  <Route
                    path="/clip-preview/:shareToken"
                    element={<ClipPreview />}
                  />
                  <Route
                    path="/dashboard/*"
                    element={
                      <ProtectedRoute>
                        <UserPortal />
                      </ProtectedRoute>
                    }
                  />
                  {/* /recording-view/:meetingId — single-layout web recorder route.
                      Agora's bot Chrome appears to whitelist this URL pattern for
                      window.navigator.notifyReady(); URLs like /recording-view-lite-v*
                      have not worked with onhold:true (3 separate confirmed 0-byte
                      tests). To use onhold:true with the lite-v3 page (and thus
                      eliminate the startup white-screen window), the multi-web
                      fan-out routes through THIS path with ?lite=v3 in the query
                      string and we conditionally render the lite-v3 component. */}
                  <Route path="/recording-view/:meetingId" element={
                    <Suspense fallback={<div style={{width:'100vw',height:'100vh',background:'linear-gradient(135deg,#060621 0%,#0a0a2e 50%,#060621 100%)',display:'flex',alignItems:'center',justifyContent:'center'}}><img src={DEFAULT_LOGO} alt={DEFAULT_LOGO_ALT} style={{width:200,opacity:0.8}} /></div>}>
                      {new URLSearchParams(window.location.search).get('lite') === 'v3'
                        ? <RecordingViewLiteV3 />
                        : <RecordingView />}
                    </Suspense>
                  } />
                  <Route path="/recording-view-grid/:meetingId" element={<Conference forceGrid />} />
                  <Route path="/recording-view-lite/:meetingId" element={
                    <Suspense fallback={<div style={{width:'100vw',height:'100vh',background:'#001233',display:'flex',alignItems:'center',justifyContent:'center'}}><img src={DEFAULT_LOGO} alt={DEFAULT_LOGO_ALT} style={{width:200,opacity:0.7}} /></div>}>
                      <RecordingViewLite />
                    </Suspense>
                  } />
                  {/* Lite v2 — alias of the standard meeting URL but signals
                      recorder-lite mode to AgoraReactContext (skips VB extension)
                      and to the rest of the bundle (skips heavy SDK paths).
                      Reuses Conference + MeetingContainerNew with isRecorderMode
                      already auto-set by URL params, so all overlays / elements
                      / reactions / participant grid render exactly as in the
                      proven /<roomId>?recorder=true path. */}
                  <Route path="/recording-view-lite-v2/:meetingId" element={<Conference />} />
                  {/* Lite v3 — third-iteration recorder page. Strips MeetingContainer
                      entirely (the heavy 3000-line tree dropped lite-v2 to ~10
                      unique fps). Direct Agora RTC + Socket.IO + minimal overlay
                      components. Targets 25-30 unique fps and uses onhold:true
                      with a first-paint notifyReady to eliminate startup white
                      screen. See RecordingViewLiteV3.jsx for full design notes. */}
                  <Route path="/recording-view-lite-v3/:meetingId" element={
                    <Suspense fallback={<div style={{width:'100vw',height:'100vh',background:'#001233',display:'flex',alignItems:'center',justifyContent:'center'}}><img src={DEFAULT_LOGO} alt={DEFAULT_LOGO_ALT} style={{width:200,opacity:0.7}} /></div>}>
                      <RecordingViewLiteV3 />
                    </Suspense>
                  } />
                  <Route path="*" element={<Navigate to="/login" replace />} />
                </Routes>
                  </MaintenanceModeGuard>
                </EditCtaProvider>
              </MeetingProvider>
            </BrandKitProvider>
          </SiteProvider>
        </WhiteLabelProvider>
      </BrowserRouter>
    </>
  );
}

export default App;
